import React, { useEffect, useMemo, useState } from "react";
import { fetchJson } from "../../lib/api";
import {
  detectDelimiter,
  guessAmountFormat,
  guessDateFormat,
  guessFieldMapping,
  guessHasHeader,
  parseAmountValue,
  parseCsvRows,
  parseDateValue,
  type AmountFormat,
  type CsvDelimiter,
  type DateFormat,
} from "../../lib/csv-utils";
import type { BankAccount } from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Input } from "../input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../table";

export const BankTransactionsImport = ({
  houseId,
  search,
  setSearch,
  navigate,
  onImported,
  houseRole,
}: {
  houseId: number | null;
  search: string;
  setSearch: (nextSearch: string, options?: { replace?: boolean }) => void;
  navigate: (path: string, search?: string) => void;
  onImported: (message: string) => void;
  houseRole: "readonly" | "full";
}) => {
  const { t } = useI18n();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const accountParam = Number.parseInt(searchParams.get("account") ?? "", 10);
  const selectedAccountId = Number.isNaN(accountParam) ? null : accountParam;
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [rawRows, setRawRows] = useState<string[][]>([]);
  const [delimiter, setDelimiter] = useState<CsvDelimiter>(";");
  const [hasHeader, setHasHeader] = useState(false);
  const [mapping, setMapping] = useState<{
    date: number | null;
    description: number | null;
    amount: number | null;
  }>({ date: null, description: null, amount: null });
  const isReadonly = houseRole === "readonly";
  const [dateFormat, setDateFormat] = useState<DateFormat | null>(null);
  const [amountFormat, setAmountFormat] = useState<AmountFormat>(null);
  const [error, setError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const loadAccounts = async () => {
    if (!houseId) {
      setAccounts([]);
      return;
    }
    try {
      const response = await fetchJson<{ accounts: BankAccount[] }>(
        `/api/houses/${houseId}/bank-accounts`,
      );
      setAccounts(response.accounts);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : t("bankTransactions.loadAccountsFailed", "Failed to load accounts"),
      );
    }
  };

  useEffect(() => {
    void loadAccounts();
  }, [houseId]);

  useEffect(() => {
    if (!accounts.length) {
      return;
    }
    if (selectedAccountId && accounts.some((account) => account.id === selectedAccountId)) {
      return;
    }
    const nextParams = new URLSearchParams(search);
    nextParams.set("account", String(accounts[0].id));
    setSearch(`?${nextParams.toString()}`, { replace: true });
  }, [accounts, search, selectedAccountId, setSearch]);

  useEffect(() => {
    if (!rawRows.length) {
      setMapping({ date: null, description: null, amount: null });
      return;
    }
    const headers = hasHeader ? rawRows[0] : null;
    const dataRows = hasHeader ? rawRows.slice(1) : rawRows;
    setMapping(guessFieldMapping(headers, dataRows));
  }, [hasHeader, rawRows]);

  useEffect(() => {
    if (!rawRows.length) {
      setDateFormat(null);
      setAmountFormat(null);
      return;
    }
    const dataRows = hasHeader ? rawRows.slice(1) : rawRows;
    if (mapping.date !== null) {
      const dateValues = dataRows
        .map((row) => row[mapping.date ?? 0] ?? "")
        .filter((value) => value);
      setDateFormat(guessDateFormat(dateValues));
    } else {
      setDateFormat(null);
    }
    if (mapping.amount !== null) {
      const amountValues = dataRows
        .map((row) => row[mapping.amount ?? 0] ?? "")
        .filter((value) => value);
      setAmountFormat(guessAmountFormat(amountValues));
    } else {
      setAmountFormat(null);
    }
  }, [hasHeader, mapping.amount, mapping.date, rawRows]);

  if (!houseId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("bankTransactions.importTitle", "Import transactions")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted">
            {t("bankTransactions.importSelectHouse", "Select a house to import transactions.")}
          </p>
        </CardContent>
      </Card>
    );
  }

  const selectedAccount = accounts.find((account) => account.id === selectedAccountId) ?? null;
  const selectedBankAccountId = selectedAccount?.id ?? null;
  const headerRow = hasHeader ? rawRows[0] : null;
  const dataRows = hasHeader ? rawRows.slice(1) : rawRows;
  const columnCount = rawRows.reduce((max, row) => Math.max(max, row.length), 0);
  const columns = Array.from({ length: columnCount }, (_, index) =>
    headerRow?.[index] ? headerRow[index] : t("bankTransactions.column", "Column {index}", { index: index + 1 }),
  );
  const previewRows = dataRows.slice(0, 5);
  const parsedTransactions = useMemo(() => {
    if (mapping.date === null || mapping.description === null || mapping.amount === null) {
      return [];
    }
    if (!dateFormat) {
      return [];
    }
    const dateColumnIndex = mapping.date;
    const descriptionColumnIndex = mapping.description;
    const amountColumnIndex = mapping.amount;
    return dataRows
      .map((row) => {
        const transactionDate = parseDateValue(row[dateColumnIndex] ?? "", dateFormat);
        const amount = parseAmountValue(row[amountColumnIndex] ?? "", amountFormat);
        if (!transactionDate || amount === null) {
          return null;
        }
        return {
          transactionDate,
          description: row[descriptionColumnIndex] ?? "",
          amount,
        };
      })
      .filter((row) => row !== null);
  }, [amountFormat, dataRows, dateFormat, mapping.amount, mapping.date, mapping.description]);

  const mappingValid =
    Boolean(selectedAccount) &&
    mapping.date !== null &&
    mapping.description !== null &&
    mapping.amount !== null &&
    Boolean(dateFormat) &&
    parsedTransactions.length > 0;

  const handleFile = async (file: File) => {
    setError(null);
    const text = await file.text();
    const nextDelimiter = detectDelimiter(text);
    const rows = parseCsvRows(text, nextDelimiter);
    setDelimiter(nextDelimiter);
    setRawRows(rows);
    setHasHeader(guessHasHeader(rows));
  };

  const importTransactions = async () => {
    if (isReadonly) {
      return;
    }
    if (!selectedBankAccountId || !mappingValid || !selectedAccount) {
      return;
    }
    setIsImporting(true);
    setError(null);
    try {
      const response = await fetchJson<{ inserted: number; total: number }>(
        `/api/houses/${houseId}/bank-transactions/import`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            bankAccountId: selectedBankAccountId,
            transactions: parsedTransactions.map((row) => ({
              ...row,
              currency: selectedAccount.currency,
            })),
          }),
        },
      );
      onImported(
        t("bankTransactions.importedRows", "Imported {inserted} of {total} rows.", {
          inserted: response.inserted,
          total: response.total,
        }),
      );
      navigate("/bank-transactions", search);
    } catch (importError) {
      setError(
        importError instanceof Error ? importError.message : t("bankTransactions.importFailed", "Import failed"),
      );
    } finally {
      setIsImporting(false);
    }
  };

  const updateMapping = (field: "date" | "description" | "amount", value: string) => {
    const index = Number.parseInt(value, 10);
    setMapping((current) => ({
      ...current,
      [field]: Number.isNaN(index) ? null : index,
    }));
  };

  const updateAccount = (value: string) => {
    const nextParams = new URLSearchParams(search);
    nextParams.set("account", value);
    setSearch(`?${nextParams.toString()}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-lg font-semibold text-ink">
            {t("bankTransactions.importTitle", "Import transactions")}
          </p>
          <p className="text-sm text-muted">
            {t("bankTransactions.importSubtitle", "Upload a CSV export and map the columns.")}
          </p>
        </div>
      </div>
      {isReadonly ? (
        <p className="rounded-md border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {t(
            "bankTransactions.importReadonly",
            "This house is read-only. Importing transactions is disabled.",
          )}
        </p>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t("bankTransactions.importSettings", "Import settings")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <label className="text-sm font-medium text-ink" htmlFor="import-account-select">
                {t("bankTransactions.bankAccount", "Bank account")}
              </label>
              <select
                id="import-account-select"
                className="h-9 rounded-md border px-3 text-sm brand-input brand-focus"
              value={selectedBankAccountId ? String(selectedBankAccountId) : ""}
                onChange={(event) => updateAccount(event.target.value)}
                disabled={!accounts.length || isReadonly}
              >
                <option value="" disabled>
                  {t("bankTransactions.selectBankAccount", "Select bank account")}
                </option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} · {account.number} · {account.currency}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-ink" htmlFor="csv-upload">
                {t("bankTransactions.csvFile", "CSV file")}
              </label>
              <Input
                id="csv-upload"
                type="file"
                accept=".csv,text/csv"
                disabled={isReadonly}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    void handleFile(file);
                  }
                }}
              />
            </div>
          </div>
          {rawRows.length ? (
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted">
              <span>
                {t("bankTransactions.detectedDelimiter", "Detected delimiter: {delimiter}", {
                  delimiter: delimiter === "\t" ? t("bankTransactions.tab", "Tab") : delimiter,
                })}
              </span>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={hasHeader}
                  onChange={(event) => setHasHeader(event.target.checked)}
                  disabled={isReadonly}
                />
                {t("bankTransactions.hasHeader", "File includes header row")}
              </label>
              {dateFormat ? (
                <span>
                  {t("bankTransactions.detectedDateFormat", "Detected date format: {format}", {
                    format: dateFormat,
                  })}
                </span>
              ) : null}
              {amountFormat ? (
                <span>
                  {t("bankTransactions.detectedAmountFormat", "Detected amount format: {format}", {
                    format: amountFormat === "comma" ? "1.234,56" : "1,234.56",
                  })}
                </span>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("bankTransactions.fieldMapping", "Field mapping")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {rawRows.length === 0 ? (
            <p className="text-sm text-muted">
              {t("bankTransactions.mapFieldsHint", "Upload a CSV file to map fields.")}
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-1">
                <label className="text-sm font-medium text-ink" htmlFor="map-date">
                  {t("common.date", "Date")}
                </label>
                <select
                  id="map-date"
                  className="h-9 w-full rounded-md border px-3 text-sm brand-input brand-focus"
                  value={mapping.date ?? ""}
                  onChange={(event) => updateMapping("date", event.target.value)}
                  disabled={isReadonly}
                >
                  <option value="">{t("bankTransactions.selectColumn", "Select column")}</option>
                  {columns.map((column, index) => (
                    <option key={`${column}-date`} value={index}>
                      {column}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-ink" htmlFor="map-description">
                  {t("bankTransactions.description", "Description")}
                </label>
                <select
                  id="map-description"
                  className="h-9 w-full rounded-md border px-3 text-sm brand-input brand-focus"
                  value={mapping.description ?? ""}
                  onChange={(event) => updateMapping("description", event.target.value)}
                  disabled={isReadonly}
                >
                  <option value="">{t("bankTransactions.selectColumn", "Select column")}</option>
                  {columns.map((column, index) => (
                    <option key={`${column}-description`} value={index}>
                      {column}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-ink" htmlFor="map-amount">
                  {t("bankTransactions.amount", "Amount")}
                </label>
                <select
                  id="map-amount"
                  className="h-9 w-full rounded-md border px-3 text-sm brand-input brand-focus"
                  value={mapping.amount ?? ""}
                  onChange={(event) => updateMapping("amount", event.target.value)}
                  disabled={isReadonly}
                >
                  <option value="">{t("bankTransactions.selectColumn", "Select column")}</option>
                  {columns.map((column, index) => (
                    <option key={`${column}-amount`} value={index}>
                      {column}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {rawRows.length ? (
            <div className="space-y-2">
              <p className="text-sm text-muted">
                {mappingValid
                  ? t("bankTransactions.readyToImport", "Ready to import {count} rows.", {
                      count: parsedTransactions.length,
                    })
                  : t(
                      "bankTransactions.mapFields",
                      "Map date, description, and amount to continue.",
                    )}
              </p>
              {previewRows.length ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      {columns.map((column) => (
                        <TableHead key={column}>{column}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewRows.map((row, rowIndex) => (
                      <TableRow key={`preview-${rowIndex}`}>
                        {columns.map((_, columnIndex) => (
                          <TableCell key={`${rowIndex}-${columnIndex}`}>
                            {row[columnIndex] ?? ""}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : null}
            </div>
          ) : null}
          {error ? <p className="text-sm text-error">{error}</p> : null}
          <div className="flex justify-end">
            <Button
              onClick={importTransactions}
              disabled={!mappingValid || isImporting || isReadonly}
            >
              {isImporting
                ? t("bankTransactions.importing", "Importing...")
                : t("bankTransactions.importTransactions", "Import transactions")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
